import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabaseClient';
import DashboardLayout from './DashboardLayout';
import styles from './MDDashboard.module.css';
import { 
  FiBriefcase, FiTarget, FiUsers, FiTrendingUp,
  FiCalendar, FiClipboard, FiCheckCircle, FiAlertCircle,
  FiBarChart2, FiSend, FiPlus, FiX
} from 'react-icons/fi';

const MDDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [tasksAssigned, setTasksAssigned] = useState([]);
  const [strategicInitiatives, setStrategicInitiatives] = useState([]);
  const [showAssignTask, setShowAssignTask] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assigned_to: '',
    priority: 'high',
    due_date: ''
  });
  const [ceos, setCeos] = useState([]);

  useEffect(() => {
    fetchUser();
    fetchCEOs();
    fetchTasks();
    fetchStrategicInitiatives();
  }, []);

  const fetchUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();
      setUser(profile);
    }
  };

  const fetchCEOs = async () => {
    const { data } = await supabase
      .from('user_profiles')
      .select('id, name, email')
      .eq('role', 'CEO');
    setCeos(data || []);
  };

  const fetchTasks = async () => {
    if (!user?.id) return;
    const { data } = await supabase
      .from('tasks')
      .select('*, assigned_to_user:assigned_to(name)')
      .eq('assigned_by', user.id)
      .order('created_at', { ascending: false });
    setTasksAssigned(data || []);
  };

  const fetchStrategicInitiatives = async () => {
    const { data } = await supabase
      .from('strategic_initiatives')
      .select('*')
      .order('created_at', { ascending: false });
    setStrategicInitiatives(data || []);
  };

  const handleAssignTask = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from('tasks')
        .insert([{
          title: newTask.title,
          description: newTask.description,
          assigned_by: user.id,
          assigned_to: newTask.assigned_to,
          priority: newTask.priority,
          due_date: newTask.due_date,
          status: 'pending'
        }]);

      if (error) throw error;

      await supabase.from('notifications').insert([{
        user_id: newTask.assigned_to,
        title: 'New Task Assigned',
        message: `You have been assigned a new task: ${newTask.title}`,
        type: 'task'
      }]);

      setShowAssignTask(false);
      setNewTask({ title: '', description: '', assigned_to: '', priority: 'high', due_date: '' });
      fetchTasks();
      alert('Task assigned successfully!');
    } catch (error) {
      console.error('Error assigning task:', error);
      alert('Error assigning task');
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="MD">
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="MD">
      <div className={styles.mdDashboard}>
        <div className={styles.header}>
          <h1>Managing Director's Dashboard</h1>
          <p>Strategic Planning & Leadership Oversight</p>
        </div>

        <div className={styles.quickActions}>
          <button onClick={() => setShowAssignTask(true)} className={styles.actionBtn}>
            <FiPlus /> Assign Task to CEO
          </button>
        </div>

        <div className={styles.sectionCard}>
          <h2><FiClipboard /> Tasks Assigned to CEO</h2>
          <div className={styles.tasksTable}>
            <table>
              <thead>
                <tr><th>Task</th><th>Assigned To</th><th>Priority</th><th>Due Date</th><th>Status</th></tr>
              </thead>
              <tbody>
                {tasksAssigned.map(task => (
                  <tr key={task.id}>
                    <td><strong>{task.title}</strong><br/><small>{task.description}</small></td>
                    <td>{task.assigned_to_user?.name || 'CEO'}</td>
                    <td><span className={`${styles.priorityBadge} ${styles[task.priority]}`}>{task.priority}</span></td>
                    <td>{new Date(task.due_date).toLocaleDateString()}</td>
                    <td><span className={`${styles.statusBadge} ${styles[task.status]}`}>{task.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {showAssignTask && (
          <div className={styles.modalOverlay} onClick={() => setShowAssignTask(false)}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h2>Assign Task to CEO</h2>
                <button onClick={() => setShowAssignTask(false)}><FiX /></button>
              </div>
              <form onSubmit={handleAssignTask} className={styles.modalForm}>
                <input type="text" placeholder="Task Title" required value={newTask.title} onChange={(e) => setNewTask({...newTask, title: e.target.value})} />
                <textarea placeholder="Description" rows="3" value={newTask.description} onChange={(e) => setNewTask({...newTask, description: e.target.value})} />
                <select required value={newTask.assigned_to} onChange={(e) => setNewTask({...newTask, assigned_to: e.target.value})}>
                  <option value="">Select CEO</option>
                  {ceos.map(ceo => <option key={ceo.id} value={ceo.id}>{ceo.name}</option>)}
                </select>
                <select value={newTask.priority} onChange={(e) => setNewTask({...newTask, priority: e.target.value})}>
                  <option value="low">Low</option><option value="medium">Medium</option>
                  <option value="high">High</option><option value="critical">Critical</option>
                </select>
                <input type="date" required value={newTask.due_date} onChange={(e) => setNewTask({...newTask, due_date: e.target.value})} />
                <div className={styles.modalActions}>
                  <button type="button" onClick={() => setShowAssignTask(false)}>Cancel</button>
                  <button type="submit">Assign Task</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MDDashboard;